import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

export interface Contact {
  id: string;
  org_id: string;
  client_id: string;
  site_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  role: string | null;
  contact_type: string | null;
  is_primary: boolean;
  sf_contact_id: string | null;
  extn: Record<string, any>;
  created_at: string;
}

export interface ContactFormData {
  client_id: string;
  site_id?: string | null;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  role?: string;
  contact_type?: string;
  is_primary?: boolean;
  sf_contact_id?: string;
}

/**
 * Get user's organization ID
 */
const getUserOrgId = async (): Promise<string | null> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return null;

    const { data, error } = await supabase
      .from("base_organisation_members")
      .select("organisation_id")
      .eq("user_id", session.user.id)
      .limit(1)
      .single();

    if (error) {
      console.error("Error getting org ID:", error);
      return null;
    }

    return data?.organisation_id || null;
  } catch (error) {
    console.error("Error in getUserOrgId:", error);
    return null;
  }
};

/**
 * Get all contacts for the user's organization — paginated to bypass Supabase 1000-row cap
 */
export const getContacts = async (): Promise<{
  success: boolean;
  data?: Contact[];
  error?: string;
}> => {
  try {
    const orgId = await getUserOrgId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const PAGE = 1000;
    const all: Contact[] = [];
    let page = 0;

    while (true) {
      const { data, error } = await supabase
        .from("contact")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        // tie-break on the pk: created_at alone is not unique (a bulk import
        // gives thousands of rows the same timestamp), and range paging over a
        // non-deterministic order silently duplicates and skips rows
        .order("id")
        .range(page * PAGE, (page + 1) * PAGE - 1);

      if (error) throw error;
      if (!data?.length) break;
      all.push(...data);
      if (data.length < PAGE) break;
      page++;
    }

    return { success: true, data: all };
  } catch (error: any) {
    console.error("Error fetching contacts:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get contacts for a specific client
 */
export const getContactsByClient = async (
  clientId: string
): Promise<{
  success: boolean;
  data?: Contact[];
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from("contact")
      .select("*")
      .eq("client_id", clientId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error("Error fetching contacts:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single contact by ID
 */
export const getContactById = async (
  id: string
): Promise<{
  success: boolean;
  data?: Contact;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from("contact")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error fetching contact:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a new contact
 */
export const createContact = async (
  formData: ContactFormData
): Promise<{
  success: boolean;
  data?: Contact;
  error?: string;
}> => {
  try {
    const orgId = await getUserOrgId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data, error } = await supabase
      .from("contact")
      .insert({
        org_id: orgId,
        client_id: formData.client_id,
        site_id: formData.site_id || null,
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        email: formData.email || null,
        phone: formData.phone || null,
        mobile: formData.mobile || null,
        role: formData.role || null,
        contact_type: formData.contact_type || null,
        is_primary: formData.is_primary ?? false,
        sf_contact_id: formData.sf_contact_id || null,
        extn: {},
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error creating contact:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing contact
 */
export const updateContact = async (
  id: string,
  formData: Partial<ContactFormData>
): Promise<{
  success: boolean;
  data?: Contact;
  error?: string;
}> => {
  try {
    const updateData: any = {};

    if (formData.client_id !== undefined) updateData.client_id = formData.client_id;
    if (formData.site_id !== undefined) updateData.site_id = formData.site_id || null;
    if (formData.first_name !== undefined) updateData.first_name = formData.first_name || null;
    if (formData.last_name !== undefined) updateData.last_name = formData.last_name || null;
    if (formData.email !== undefined) updateData.email = formData.email || null;
    if (formData.phone !== undefined) updateData.phone = formData.phone || null;
    if (formData.mobile !== undefined) updateData.mobile = formData.mobile || null;
    if (formData.role !== undefined) updateData.role = formData.role || null;
    if (formData.contact_type !== undefined) updateData.contact_type = formData.contact_type || null;
    if (formData.is_primary !== undefined) updateData.is_primary = formData.is_primary;
    if (formData.sf_contact_id !== undefined) updateData.sf_contact_id = formData.sf_contact_id || null;

    const { data, error } = await supabase
      .from("contact")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error updating contact:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a contact
 */
export const deleteContact = async (
  id: string
): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase.from("contact").delete().eq("id", id);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error("Error deleting contact:", error);
    return { success: false, error: error.message };
  }
};

